import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Building2, CheckCircle2, Clock3, ExternalLink, RefreshCw } from "lucide-react";


type AccountStatus = "current" | "warning" | "action_required" | "critical";
type AppStatus = AccountStatus | "unavailable";

type BankAccount = {
  key: string;
  bankName: string;
  accountName: string;
  accountSuffix: string | null;
  connectionType: "finapi" | "qonto" | "other";
  lastSuccessAt: string | null;
  ageHours: number | null;
  status: AccountStatus;
  message: string | null;
};

type BankApp = {
  app: { key: string; name: string; scope: string; route: string };
  checkedAt: string;
  status: AppStatus;
  summary: { accounts: number; current: number; warning: number; critical: number; actionRequired: number };
  accounts: BankAccount[];
  message: string | null;
};

type BankSnapshot = {
  checkedAt: string;
  status: AppStatus;
  summary: { apps: number; accounts: number; current: number; warning: number; critical: number; actionRequired: number; unavailable: number };
  apps: BankApp[];
};

const STATUS_META: Record<AppStatus, { label: string; className: string }> = {
  current: { label: "Aktuell", className: "is-ok" },
  warning: { label: "Überfällig", className: "is-warn" },
  action_required: { label: "Aktion erforderlich", className: "is-bad" },
  critical: { label: "Veraltet", className: "is-bad" },
  unavailable: { label: "Nicht erreichbar", className: "is-bad" },
};

function formatTimestamp(value: string | null): string {
  if (!value) return "Noch kein erfolgreicher Stand";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Zeitpunkt unbekannt";
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/Berlin",
  }).format(parsed);
}

function ageLabel(hours: number | null): string {
  if (hours === null) return "nicht verfügbar";
  if (hours < 1) return "vor weniger als 1 Stunde";
  if (hours < 24) return `vor ${Math.round(hours)} Stunden`;
  const days = Math.round((hours / 24) * 10) / 10;
  return `vor ${days.toLocaleString("de-DE")} Tagen`;
}

function StatusBadge({ status }: { status: AppStatus }) {
  const meta = STATUS_META[status];
  return <span className={`cc-badge-st ${meta.className}`}>{meta.label}</span>;
}

function Metric({ label, value, tone = "neutral" }: { label: string; value: number; tone?: "neutral" | "ok" | "warn" | "bad" }) {
  const color = tone === "ok" ? "var(--color-success, #22c55e)" : tone === "warn" ? "var(--color-warning, #f59e0b)" : tone === "bad" ? "var(--color-danger, #ef4444)" : "var(--text)";
  return (
    <div className="cc-card" style={{ minWidth: 0 }}>
      <div className="cc-card-body" style={{ padding: "16px 18px" }}>
        <div style={{ color: "var(--text-muted)", fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase" }}>{label}</div>
        <div style={{ marginTop: 6, color, fontSize: 26, fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      </div>
    </div>
  );
}

function AccountRow({ account }: { account: BankAccount }) {
  return (
    <div
      data-testid={`bank-account-${account.key}`}
      className="banken-account-row"
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ color: "var(--text)", fontSize: 13.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{account.bankName}</div>
        <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {account.accountName}{account.accountSuffix ? ` · •••• ${account.accountSuffix}` : ""}
        </div>
      </div>
      <div>
        <div style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em" }}>Anbindung</div>
        <div style={{ color: "var(--text)", fontSize: 12.5, marginTop: 4 }}>{account.connectionType === "qonto" ? "Qonto" : account.connectionType === "finapi" ? "finAPI" : "Bank-API"}</div>
      </div>
      <div>
        <div style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em" }}>Letzter Erfolg</div>
        <div style={{ color: "var(--text)", fontSize: 12.5, marginTop: 4 }}>{formatTimestamp(account.lastSuccessAt)}</div>
        <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 2 }}>{ageLabel(account.ageHours)}</div>
      </div>
      <div style={{ justifySelf: "end", textAlign: "right" }}>
        <StatusBadge status={account.status} />
        {account.message && <div style={{ color: account.status === "warning" ? "var(--color-warning, #f59e0b)" : "var(--color-danger, #ef4444)", fontSize: 11, maxWidth: 260, marginTop: 5 }}>{account.message}</div>}
      </div>
    </div>
  );
}

export function BankenPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<BankSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    else setLoading(true);
    try {
      const response = await fetch("/api/banks/status", { credentials: "include", cache: "no-store" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setData(await response.json() as BankSnapshot);
      setError(null);
    } catch {
      setError("Der zentrale Bankenstatus konnte nicht geladen werden.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load(false);
    const timer = window.setInterval(() => void load(true), 60_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const needsAttention = useMemo(() => data && (data.summary.warning + data.summary.critical + data.summary.actionRequired + data.summary.unavailable > 0), [data]);
  const attentionCount = data ? data.summary.warning + data.summary.critical + data.summary.actionRequired + data.summary.unavailable : 0;

  return (
    <div className="hub-page" data-testid="banken-page">
      <header className="hub-page-header">
        <div className="hub-page-header-main">
          <div className="hub-eyebrow">FINANZDATEN · READ-ONLY</div>
          <h1 className="hub-title">Banken</h1>
          <p className="hub-sub">Zentrale Kontrolle aller Bankanbindungen. Keine Beträge, keine Kontobewegungen und keine Änderungen an bestehenden Verbindungen.</p>
        </div>
        <button className="cc-btn cc-btn-secondary" onClick={() => void load(true)} disabled={refreshing} data-testid="banks-refresh">
          <RefreshCw size={15} className={refreshing ? "spin" : ""} />
          Status aktualisieren
        </button>
      </header>

      <div style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        {needsAttention && (
          <div className="cc-card" style={{ borderColor: "var(--color-danger, #ef4444)" }} data-testid="banks-attention-banner">
            <div className="cc-card-body" style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 16px" }}>
              <AlertTriangle size={18} color="var(--color-danger, #ef4444)" style={{ marginTop: 1, flex: "0 0 auto" }} />
              <div>
                <div style={{ color: "var(--text)", fontWeight: 650 }}>Handlungsbedarf bei {attentionCount} Statuspunkt{attentionCount === 1 ? "" : "en"}</div>
                <div style={{ color: "var(--text-muted)", fontSize: 12.5, marginTop: 3 }}>Die betroffene App und der konkrete Grund stehen unten. Der Hub führt selbst keine Bankaktion aus.</div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="cc-card" style={{ borderColor: "var(--color-danger, #ef4444)" }}>
            <div className="cc-card-body" style={{ color: "var(--color-danger, #ef4444)" }}>{error}</div>
          </div>
        )}

        {loading && !data ? (
          <div className="cc-card"><div className="cc-card-body">Bankenstatus wird geladen…</div></div>
        ) : data && (
          <>
            <section className="banken-metrics">
              <Metric label="Konten gesamt" value={data.summary.accounts} />
              <Metric label="Aktuell" value={data.summary.current} tone="ok" />
              <Metric label="Überfällig" value={data.summary.warning} tone={data.summary.warning ? "warn" : "neutral"} />
              <Metric label="Handlungsbedarf" value={data.summary.critical + data.summary.actionRequired + data.summary.unavailable} tone={data.summary.critical + data.summary.actionRequired + data.summary.unavailable ? "bad" : "neutral"} />
            </section>

            <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {data.apps.map((app) => (
                <article className="cc-card" key={app.app.key} data-testid={`bank-app-${app.app.key}`}>
                  <div className="cc-card-head" style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}>
                      <div style={{ width: 34, height: 34, display: "grid", placeItems: "center", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface-2, rgba(255,255,255,.03))" }}>
                        <Building2 size={17} color="var(--accent)" />
                      </div>
                      <div>
                        <div className="cc-card-title">{app.app.scope}</div>
                        <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>{app.app.name} · {app.summary.accounts} {app.summary.accounts === 1 ? "Konto" : "Konten"}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <StatusBadge status={app.status} />
                      <button className="cc-btn cc-btn-secondary" onClick={() => navigate(app.app.route)}>
                        In App öffnen <ExternalLink size={13} />
                      </button>
                    </div>
                  </div>
                  <div className="cc-card-body" style={{ paddingTop: 0 }}>
                    {app.status === "unavailable" || app.accounts.length === 0 ? (
                      <div style={{ display: "flex", gap: 9, alignItems: "center", color: "var(--text-muted)", padding: "14px 0" }}>
                        {app.status === "unavailable" ? <AlertTriangle size={15} /> : <Clock3 size={15} />}
                        {app.message || "Keine Kontodaten vorhanden."}
                      </div>
                    ) : app.accounts.map((account) => <AccountRow account={account} key={account.key} />)}
                  </div>
                </article>
              ))}
            </section>

            <div style={{ display: "flex", alignItems: "center", gap: 7, color: "var(--text-muted)", fontSize: 11.5 }}>
              <CheckCircle2 size={13} />
              Status geprüft: {formatTimestamp(data.checkedAt)} · Die Aktualisierung liest nur lokale Statusdaten aus den Apps.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
